<template>
  <div>
    <SectionSeparatorWrapper headerText="Premium payment" :separator="false" headerRequiredFieldsText="Indicates required field">
      <h3 class="h6 sub-heading-underlined">{{ editMode ? 'Update payment details' : 'Current premium payment details' }}</h3>
      <div v-html="editMode ? refData.premiumPaymentEditingSubheadingText.informationText : refData.premiumPaymentSubheadingText.informationText"></div>
      <button class="non-button-style" @click="switchMode" :disabled="showChangePaymentDetailsDisabledMessage">{{ editButtonDisplayText }}</button>
      <alert
        v-if="showChangePaymentDetailsDisabledMessage"
        alertType="info"
        message="Payment details cannot be changed from 2 days before until 3 days after your next payment date."
      />
      <!-- read only view -->
      <div v-if="!editMode" class="form-section">
        <FormField :helperText="editMode ? '' : refData.premiumPaymentCalloutText.informationText" :noLabelOverride="true">
          <div class="premium-payment-box">
            <div class="box-row">
              <span class="info-title">Payment method</span>
              <p>{{ toSentenceCase(preferredPaymentMethod) }}</p>
            </div>
            <div class="box-row">
              <span class="info-title">Payment frequency</span>
              <p>{{ toSentenceCase(initialData.premiumPaymentMethod.frequency) }}</p>
            </div>
            <div class="box-row">
              <span class="info-title">Next payment date</span>
              <p>{{ reverseFormatFormDate(initialData.nextContributionDate) }}</p>
            </div>

            <!-- Invoice details -->
            <div v-if="isInvoiceCollectionMethod" class="box-row">
              <span class="info-title">BPAY biller code</span>
              <p>{{ toSentenceCase(referenceData.premiumPaymentInvoiceBpayBillerCode.informationText) }}</p>
            </div>
            <div v-if="isInvoiceCollectionMethod" class="box-row">
              <span class="info-title">BPAY customer reference no.</span>
              <p>{{ toSentenceCase(initialData.premiumPaymentMethod.policyCrn) }}</p>
            </div>

            <!-- Bank account details -->
            <div v-if="isBankAccountPaymentMethod" class="box-row">
              <span class="info-title">BSB</span>
              <p>{{ BSBMask(initialData.premiumPaymentAccount.bsb) }}</p>
            </div>
            <div v-if="isBankAccountPaymentMethod" class="box-row">
              <span class="info-title">Account number</span>
              <p>{{ toSentenceCase(initialData.premiumPaymentAccount.accountNumberOrToken) }}</p>
            </div>
            <div v-if="isBankAccountPaymentMethod" class="box-row">
              <span class="info-title">Account name</span>
              <p>{{ initialData.premiumPaymentAccount.accountHolderName }}</p>
            </div>

            <!-- Credit card details -->
            <div v-if="isCreditCardPaymentMethod" class="box-row">
              <span class="info-title">Name on card</span>
              <p>{{ initialData.premiumPaymentAccount.accountHolderName }}</p>
            </div>
            <div v-if="isCreditCardPaymentMethod" class="box-row">
              <span class="info-title">Card number</span>
              <p>{{ initialData.premiumPaymentAccount.maskedCreditCardNumber }}</p>
            </div>
            <div v-if="isCreditCardPaymentMethod" class="box-row">
              <span class="info-title">Expiry</span>
              <p>{{ formatCreditCardExpiryDDMM(initialData.premiumPaymentAccount.expiryDate) }}</p>
            </div>
          </div>
        </FormField>
      </div>

      <!-- edit view -->
      <template v-else>
        <div class="form-section">
          <FuturePaymentsForm
            :formFieldLabels="formFieldLabels"
            :referenceData="referenceData"
            :formResponses="formResponses"
            :editMode="editMode"
            :crn="initialData.premiumPaymentMethod.policyCrn"
            @updatePaymentDetails="updatePaymentDetails"
            :initialData="initialData"
            ref="future-payments-form-premium-payment"
            @reinitialiseSelectedPaymentForm="$emit('reinitialiseSelectedPaymentForm')"
          />
        </div>
      </template>
    </SectionSeparatorWrapper>
  </div>
</template>

<script>
import { UpdatePaymentDetailsStepNames } from '@/enums';
import { CollectionMethods, PaymentMethods } from '@general/js/enums';
import { formatFormDate, removeWhitespaceFromString, reverseFormatFormDate, BSBMask, toSentenceCase } from '@general/js/utils';
import { convertCCExpiryToDate, isDateInRange } from '@/utils';
import { addDays } from 'date-fns';
import { Alert, SectionSeparatorWrapper } from '@/components/index';
import FuturePaymentsForm from './FuturePaymentsForm.vue';
import PaymentDetailsMixin from './PaymentDetailsMixin';
import useVuelidate from '@vuelidate/core';

export default {
  name: UpdatePaymentDetailsStepNames.step1,

  setup() {
    return { v$: useVuelidate() };
  },

  components: {
    FuturePaymentsForm,
    SectionSeparatorWrapper,
    Alert,
  },

  mixins: [PaymentDetailsMixin],

  props: {
    /**
     * This object contains Retrieve accounts response from EAI, which holds `premiumPaymentMethod`, `premiumPaymentAccount` & `claimsAccount`
     */
    initialData: {
      type: Object,
      required: true,
    },
    /**
     * This object contains ref data fetched from CMS BE
     */
    referenceData: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      editMode: false,
      isFormInvalid: false,
      formFieldLabels: {},
      formResponses: {
        paymentMethod: null,
        paymentFrequency: null,
        paymentStartDate: null,
        nameOnCard: '',
        isDirectDebitServiceAgreement: false,
        paymentBankBSB: null,
        paymentBankAccountNumber: null,
        paymentBankAccountName: null,
        isPaymentAcknowledgement: false,
      },
      sttLoadStatus: null,
      isCardNumberInvalid: null,
      cardNumberErrorMessage: '',
      isCardCVVInvalid: null,
      cardCVVErrorMessage: '',
      isCardExpiryInvalid: null,
      cardExpiryErrorMessage: '',
      collectionMethod: null,
    };
  },

  validations() {
    return this.validationObj;
  },

  mounted() {
    this.setFormFieldLabels();
    this.setPaymentMethodOptions();
  },

  watch: {
    editMode: {
      handler(val) {
        if (val === false) {
          // reset to initial payment details for premium payment when user selects 'remove changes'
          const obj = { ...this.paymentDetails, premiumPaymentMethod: this.initialData.premiumPaymentMethod, premiumPaymentAccount: this.initialData.premiumPaymentAccount };
          this.updatePaymentDetailsInfo(obj);
        }
      },
    },
  },

  computed: {
    editButtonDisplayText() {
      return this.editMode ? 'Cancel changes' : 'Change my payment details';
    },

    showChangePaymentDetailsDisabledMessage() {
      // Disable changing payment details the nextContributionDate is within two days into the future or lastContributionDate is within past three days (both inclusive)

      // EAI will always send a nextContributionDate, so need to prevent the user from updating details if not recieved
      // lastContributionDate can be null if the member has never made a payment, which we should allow edits in this case
      if (this.initialData?.nextContributionDate === null) {
        return true;
      }

      const checkLastContributionDate = isDateInRange(reverseFormatFormDate(this.initialData?.lastContributionDate), addDays(new Date(), -4), new Date());
      const checkNextContributionDate = isDateInRange(reverseFormatFormDate(this.initialData?.nextContributionDate), addDays(new Date(), -1), addDays(new Date(), 2));
      return checkLastContributionDate || checkNextContributionDate;
    },

    preferredPaymentMethod() {
      let preferredMethod = '';
      if (this.isBankAccountPaymentMethod) {
        preferredMethod = 'Bank account';
      } else if (this.isCreditCardPaymentMethod) {
        preferredMethod = 'Credit card';
      } else if (this.isInvoiceCollectionMethod) {
        preferredMethod = 'Invoice';
      } else {
        preferredMethod = 'Direct debit';
      }

      return preferredMethod;
    },

    validationObj() {
      // adding paymentDetails validation rule for making sure `v$.paymentDetails.$model` is accessible for watcher in `PaymentDetailsMixin`
      const obj = {
        paymentDetails: {},
      };

      // if in `editMode` , there is validation
      if (this.editMode) {
        obj.isFormInvalid = {
          isValid(bool) {
            return bool === false;
          },
        };
      }

      return obj;
    },

    isBankAccountPaymentMethod() {
      if (this.initialData.premiumPaymentAccount !== null && this.initialData.premiumPaymentMethod !== null) {
        if (
          this.initialData.premiumPaymentAccount.accountType === PaymentMethods.BANK_ACCOUNT &&
          this.initialData.premiumPaymentMethod.collectionMethod === CollectionMethods.DIRECT_DEBIT
        ) {
          return true;
        }
      }
      return false;
    },

    isCreditCardPaymentMethod() {
      if (this.initialData.premiumPaymentAccount !== null && this.initialData.premiumPaymentMethod !== null) {
        if (
          this.initialData.premiumPaymentAccount.accountType === PaymentMethods.CREDIT_CARD &&
          this.initialData.premiumPaymentMethod.collectionMethod === CollectionMethods.DIRECT_DEBIT
        ) {
          return true;
        }
      }
      return false;
    },

    isInvoiceCollectionMethod() {
      if (this.initialData.premiumPaymentMethod.collectionMethod === CollectionMethods.INVOICE) {
        return true;
      }
      return false;
    },
  },

  methods: {
    BSBMask,
    reverseFormatFormDate,
    toSentenceCase,

    switchMode() {
      if (!this.membershipInfo?.datePaidTo) {
        this.$emit('editModeTriggered', this.editMode);
        return; // don't change mode if no DPT on policy
      }
      this.editMode = !this.editMode;
      this.$emit('editModeTriggered', this.editMode);
    },

    setFormFieldLabels() {
      this.formFieldLabels = {
        changePaymentMethodOrFrequencyText: 'I’d like to change my payment method, start date or payment frequency',
        paymentMethodLabelText: 'I’d like to pay by',
        frequencyLabelText: 'I’d like to pay my premium',
        startPaymentsLabelText: 'Next payment date',
      };
    },

    setPaymentMethodOptions() {
      if (!this.isInvoiceCollectionMethod) {
        this.referenceData.paymentMethodOptions = this.referenceData.paymentMethodOptions.filter(function (obj) {
          // remove invoice from payment method options if the initial payment method is not invoice.
          return obj.returnValue !== CollectionMethods.INVOICE;
        });
      }
    },

    async submitPaymentForm() {
      await this.$refs['future-payments-form-premium-payment'].submitPaymentForm();
    },

    updatePaymentDetails(data) {
      this.isFormInvalid = data.isPaymentsInvalid;

      // copy all values of payments
      Object.assign(this.formResponses, data.paymentsObj);

      let premiumPaymentAccount;

      // Form premiumPaymentAccount request payload
      if (this.formResponses.paymentMethod === PaymentMethods.BANK_ACCOUNT) {
        premiumPaymentAccount = {
          id: this.initialData?.premiumPaymentAccount?.id ?? null,
          accountType: this.formResponses.paymentMethod,
          accountHolderName: this.formResponses.paymentBankAccountName,
          accountNumberOrToken: this.formResponses.paymentBankAccountNumber,
          bsb: removeWhitespaceFromString(this.formResponses.paymentBankBSB),
          directDebitAgreement: this.formResponses.isDirectDebitServiceAgreement,
        };
        // Add previousCreditCardToken if previous method was CC
        if (this.initialData?.premiumPaymentAccount?.accountType === PaymentMethods.CREDIT_CARD) {
          premiumPaymentAccount.previousCreditCardToken = this.initialData?.premiumPaymentAccount?.accountNumberOrToken;
        }
      } else if (this.formResponses.paymentMethod === PaymentMethods.CREDIT_CARD) {
        let expiryDate = null;
        if (this.formResponses?.tokenResponse?.expiryMonth && this.formResponses?.tokenResponse?.expiryYear) {
          expiryDate = convertCCExpiryToDate(`${this.formResponses?.tokenResponse?.expiryMonth} / ${this.formResponses?.tokenResponse?.expiryYear}`); // Format "mm / yy" to "yyyy-MM-dd", where dd is set to the last day of the month
        }

        premiumPaymentAccount = {
          id: this.initialData?.premiumPaymentAccount?.id ?? null,
          accountType: this.formResponses.paymentMethod,
          accountHolderName: this.formResponses.nameOnCard,
          accountNumberOrToken: this.formResponses?.tokenResponse?.creditCardToken,
          maskedCreditCardNumber: this.formResponses?.tokenResponse?.truncatedCard,
          expiryDate,
          cardCategory: this.formResponses?.tokenResponse?.cardCategory,
          cardSubCategory: this.formResponses?.tokenResponse?.cardSubCategory,
          instrumentType: this.formResponses?.tokenResponse?.instrumentType,
          previousCreditCardToken:
            this.initialData?.premiumPaymentAccount?.accountType === PaymentMethods.CREDIT_CARD ? this.initialData?.premiumPaymentAccount?.accountNumberOrToken : null, // Existing token if replacing an existing credit card
          directDebitAgreement: this.formResponses.isDirectDebitServiceAgreement,
        };
      } else {
        // If is invoice then send a null premium payment account
        premiumPaymentAccount = null;
      }

      // Form premiumPaymentMethod request payload
      let premiumPaymentMethod = {};

      if (this.formResponses.paymentMethod === CollectionMethods.INVOICE) {
        premiumPaymentMethod.collectionMethod = CollectionMethods.INVOICE;
      } else if (this.formResponses.paymentMethod === PaymentMethods.CREDIT_CARD || this.formResponses.paymentMethod === PaymentMethods.BANK_ACCOUNT) {
        premiumPaymentMethod.collectionMethod = CollectionMethods.DIRECT_DEBIT;
      } else {
        premiumPaymentMethod.collectionMethod = this.initialData.premiumPaymentMethod.collectionMethod;
      }

      premiumPaymentMethod = {
        ...premiumPaymentMethod,
        policyCrn: this.initialData.premiumPaymentMethod.policyCrn,
        frequency: this.formResponses.paymentFrequency?.toLowerCase(),
        paymentStartDate: formatFormDate(this.formResponses.paymentStartDate), // in yyyy-MM-dd format
      };

      const newData = {
        ...this.paymentDetails,
        premiumPaymentMethod,
        premiumPaymentAccount,
      };

      this.updatePaymentDetailsInfo(newData); // vuex update
    },
  },
};
</script>

<style lang="scss" scoped>
table {
  th {
    padding-left: 0;
  }
}
button {
  margin-bottom: 30px;
}

:deep(.non-button-style) {
  color: $CRIMSON;
}

.premium-payment-box {
  & > * {
    padding-right: 1.5rem;
    margin-bottom: 0.75rem;

    &:first-child {
      padding-top: 1.5rem;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  .info-title {
    display: block;
    font-weight: $BOLD;
    margin-bottom: 5px;
  }

  .box-row {
    @include grid-layout;
    @include grid-template(1fr 1fr);
    margin-bottom: 0.75rem;

    & > * {
      margin-bottom: 0;
    }
  }

  .box-row:last-child {
    padding-bottom: 1.5rem;
    margin-bottom: 0;
  }

  p {
    margin-bottom: 0;
  }
}
</style>
